'use client';

import {
  Box,
  WithBadge,
  SystemIcon,
  Typography,
  IconButton,
  PlainTooltip,
} from '@wishket/design-system';
import { useRef, useState } from 'react';
import { useClickOutside } from '@wishket/yogokit';

import CollectionScrapTooltip from './CollectionQuickMenu.parts';

import { PATH } from '@/shared/constants';
import { ShareBox } from '@/shared/Components';
import { openNavigatorShare } from '@/shared/utils';

type MenuStatus = {
  isActive: boolean;
  count: number;
};

interface CollectionQuickMenuProps {
  shareId: number;
  isOwner?: boolean;
  shareTitle: string;
  likeStatus: MenuStatus;
  scrapStatus: MenuStatus;
  isScrapTooltipOpen: boolean;
  toggleLike?: () => void;
  toggleScrap?: () => void;
  closeScrapTooltip: () => void;
}

const CollectionQuickMenu = ({
  isOwner,
  shareId,
  shareTitle,
  likeStatus,
  scrapStatus,
  isScrapTooltipOpen,
  toggleLike,
  toggleScrap,
  closeScrapTooltip,
}: CollectionQuickMenuProps) => {
  const shareBoxRef = useRef<HTMLDivElement>(null);
  const scrapTooltipRef = useRef<HTMLDivElement>(null);
  const [isShareBoxOpen, setIsShareBoxOpen] = useState(false);

  useClickOutside(shareBoxRef, () => setIsShareBoxOpen(false));
  useClickOutside(scrapTooltipRef, () => closeScrapTooltip());

  const onShareSubmit = () => {
    setIsShareBoxOpen(false);
  };
  const toggleShareButton = () => {
    closeScrapTooltip();
    setIsShareBoxOpen(!isShareBoxOpen);
  };
  const toggleScrapButton = () => {
    setIsShareBoxOpen(false);
    if (toggleScrap) toggleScrap();
  };

  const openMobileShare = () => {
    openNavigatorShare({
      url: `${process.env.NEXT_PUBLIC_APP_BASE_URL}${PATH.COLLECTION}/${shareId}`,
      title: shareTitle,
    });
  };

  return (
    <Box>
      <Box className="hidden desktop:block">
        <Box className="flex flex-col gap-4">
          <WithBadge text={likeStatus.count} showZero variant="gray">
            <PlainTooltip text="좋아요" variant="left" className="w-fit">
              <IconButton outline onClick={toggleLike} size="lg">
                <SystemIcon name="large_like" solid={likeStatus.isActive} />
              </IconButton>
            </PlainTooltip>
          </WithBadge>
          {!isOwner && (
            <Box className="relative h-fit w-fit">
              <PlainTooltip
                text={scrapStatus.isActive ? '스크랩됨' : '스크랩'}
                variant="left"
                className="w-fit"
                disabled={isScrapTooltipOpen}
              >
                <IconButton outline onClick={toggleScrapButton} size="lg">
                  <SystemIcon name="large_scrap" solid={scrapStatus.isActive} />
                </IconButton>
              </PlainTooltip>
              <CollectionScrapTooltip
                ref={scrapTooltipRef}
                isOpen={isScrapTooltipOpen}
              />
            </Box>
          )}
          <Box className="relative">
            <PlainTooltip
              text="공유하기"
              variant="left"
              className="w-fit"
              disabled={isShareBoxOpen}
            >
              <IconButton outline onClick={toggleShareButton} size="lg">
                <SystemIcon name="large_share" />
              </IconButton>
            </PlainTooltip>
            {isShareBoxOpen && (
              <Box className="absolute right-full top-0 z-[15] mr-2">
                <Box onClick={onShareSubmit}>
                  <ShareBox
                    ref={shareBoxRef}
                    title={shareTitle}
                    url={`${process.env.NEXT_PUBLIC_APP_BASE_URL}${PATH.COLLECTION}/${shareId}`}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box className="desktop:hidden">
        <Box className="flex w-full items-center justify-between border-t bg-white px-5 py-5">
          <Box className="flex items-center gap-2">
            <button onClick={toggleLike}>
              <SystemIcon name="large_like" solid={likeStatus.isActive} />
            </button>
            <Typography variant="body1" className="text-w-gray-900">
              {likeStatus.count}
            </Typography>
          </Box>
          <Box className="flex gap-5">
            {!isOwner && (
              <Box className="relative flex h-fit w-fit items-center">
                <button onClick={toggleScrapButton} className="h-fit w-fit">
                  <SystemIcon name="large_scrap" solid={scrapStatus.isActive} />
                </button>
                <CollectionScrapTooltip
                  ref={scrapTooltipRef}
                  isOpen={isScrapTooltipOpen}
                />
              </Box>
            )}
            <button onClick={openMobileShare}>
              <SystemIcon name="large_share" />
            </button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CollectionQuickMenu;
